import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, UserCircle, Phone, Lock, Plus } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateClient = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine if called from handler settings
  const isHandler = location.pathname.includes('/handler/');
  const backRoute = isHandler ? '/handler-dashboard' : '/admin-settings';
  const apiEndpoint = isHandler
    ? `${API}/handler/clients/create`
    : `${API}/admin/clients/create`;
    
  const handleBackClick = () => {
    if (isHandler) {
      navigate(backRoute, { replace: true, state: { bottomTab: 'settings' } });
    } else {
      navigate(backRoute, { replace: true });
    }
  };

  const [formData, setFormData] = useState({
    username: '',
    phone: '',
    password: ''
  });
  const [creating, setCreating] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.username.trim()) {
      toast.error('Name is required');
      return;
    }

    if (!formData.phone.trim()) {
      toast.error('Phone number is required');
      return;
    }

    if (formData.phone.length !== 10) {
      toast.error('Phone must be 10 digits');
      return;
    }

    if (!formData.password.trim()) {
      toast.error('Password is required');
      return;
    }

    if (formData.password.length < 5) {
      toast.error('Password must be at least 5 characters');
      return;
    }

    try {
      setCreating(true);
      
      await axios.post(apiEndpoint, {
        username: formData.username.trim(),
        phone: formData.phone.trim(),
        password: formData.password.trim()
      });

      toast.success('✅ Client created successfully!');
      toast.info(`Phone: ${formData.phone} | Password: ${formData.password}`, {
        duration: 5000
      });

      setFormData({
        username: '',
        phone: '',
        password: ''
      });

      setCreating(false);
    } catch (error) {
      console.error('Create client failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to create client');
      setCreating(false);
    }
  };

  return (
    <div className="flex flex-col bg-gray-50 relative z-0">
      
      <div className="flex-1 overflow-y-auto p-4 flex items-center justify-center relative z-0">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-150 p-6 relative">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-3 border border-green-150 text-[#25D366]">
                <Plus size={32} weight="bold" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Create New Client</h2>
              <p className="text-sm text-gray-500 mt-1">
                Enter client details to create account
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <UserCircle size={18} weight="bold" className="inline mr-1 text-gray-400" />
                  Client Name *
                </label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="Enter client name"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-400"
                  required
                />
              </div>

              {/* Phone Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Phone size={18} weight="bold" className="inline mr-1 text-gray-400" />
                  Mobile Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="10 digit mobile number"
                  maxLength={10}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-400"
                  required
                />
                <p className="text-xs text-gray-400 mt-1.5">
                  Must be 10 digits
                </p>
              </div>

              {/* Password Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Lock size={18} weight="bold" className="inline mr-1 text-gray-400" />
                  Password *
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Minimum 5 characters"
                    className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-400"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, password: 'Abcd1234' }))}
                    className="px-4 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 text-xs font-semibold whitespace-nowrap cursor-pointer"
                  >
                    Default
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1.5">
                  Minimum 5 characters required
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={creating}
                className="w-full bg-[#25D366] text-white py-3.5 rounded-xl font-bold hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 mt-6 cursor-pointer"
              >
                <Plus size={22} weight="bold" />
                {creating ? 'Creating Client...' : 'Create Client Account'}
              </button>
            </form>

            {/* Info Box */}
            <div className="mt-6 bg-blue-50/50 border border-blue-150 rounded-xl p-4 text-blue-700 flex items-start gap-2">
              <span className="bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5">ℹ</span>
              <p className="text-xs text-blue-700 leading-relaxed">
                <strong>Note:</strong> After creation, the client can login using the phone number and password provided here.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateClient;
